/**
 * Ce que le paywall met en avant, selon l'endroit d'où il a été ouvert.
 *
 * Une seule feuille pour toutes les sources (CR-01) : seuls le titre et le
 * PREMIER avantage changent. Quelqu'un qui bute sur la limite de zones veut
 * lire « Ton potager grandit » et les zones d'abord, pas Willy. Les autres
 * avantages suivent toujours dans l'ordre de la valeur.
 */
export type PaywallBenefit = 'willy' | 'garden' | 'lunar' | 'gdd';

/** Ordre de la valeur : ce qui pèse le plus dans la décision d'abord. */
export const BENEFIT_ORDER: readonly PaywallBenefit[] = [
  'willy',
  'garden',
  'lunar',
  'gdd',
];

/**
 * Source transmise par l'écran appelant → avantage mis en tête.
 *
 * Les limites de structure arrivent aussi en `garden_<zones|beds>` depuis
 * l'aperçu du potager.
 */
const LEAD_BY_SOURCE: Record<string, PaywallBenefit> = {
  willy_chat: 'willy',
  willy_quota_reached: 'willy',
  // Bouton photo après le diagnostic offert (CR-05).
  willy_photo: 'willy',
  zone_limit: 'garden',
  bed_limit: 'garden',
  locked_structure: 'garden',
  garden_overview: 'garden',
  lunar_page: 'lunar',
  lunar_week: 'lunar',
  home_advices: 'lunar',
  home_gdd: 'gdd',
  plant_details: 'gdd',
  // Feu vert (NF-2) : les chiffres et la date probable complètent le GDD avancé.
  feu_vert: 'gdd',
};

/** Avantage mis en tête, ou `null` pour une source neutre (Compte, accueil). */
export function leadBenefitFor(source: string | null | undefined): PaywallBenefit | null {
  if (!source) return null;
  if (LEAD_BY_SOURCE[source]) return LEAD_BY_SOURCE[source];
  return source.startsWith('garden_') ? 'garden' : null;
}

/** Les quatre avantages, celui de la source en premier. */
export function benefitsFor(source: string | null | undefined): PaywallBenefit[] {
  const lead = leadBenefitFor(source);
  return lead
    ? [lead, ...BENEFIT_ORDER.filter((benefit) => benefit !== lead)]
    : [...BENEFIT_ORDER];
}

/** Clé i18n du titre. */
export function titleKeyFor(source: string | null | undefined): string {
  return `premium.paywall.titles.${leadBenefitFor(source) ?? 'default'}`;
}
